import {
  Alert,
  Button,
  Col,
  Form,
  Image,
  Row,
  Spin,
  Tag,
  Upload,
  message,
  Card,
} from "antd";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import InputRenderers from "../utils/InputRenderers";
import { io } from "socket.io-client";
import locale from "antd/es/date-picker/locale/es_ES";
import config from "../../../../config";
import { getCookie } from '../../../../services/cookies';
import { getAsset } from '../../../../services/general';
import debounce from "lodash/debounce";
import {
  arterialPresionCalculator,
  arterialWaistHipCalculators,
  imcCalculator,
  pamCalculator,
  nutritionalDiagnosis,
  bloodPressureDiagnosis,
  packsPerYearCalculator,
  bussPerryInterpretation,
  heartDiseasesCalculator,
  lungDiseasesCalculator,
  iccCalculator,
  occupationalHistoryTimeCalculator,
  pathologicalDescriptionsCalculator,
} from '../utils/Calculators';
import { handleProcedureCalculation } from '../utils/procedureCalculators';
import ECGInterpreter from '../ECGInterpreter';
import IAResults from '../IAResults';
import moment from "moment";
import FilePreviewer from "../utils/FilePreviewer";
import { calculateFormLayoutSpans, evaluateVisibility, isDependentField, buildDependentLookup, getFormColumnCount, groupDependentItems } from "../utils/formLayouts";

const SPIROMETRY_CODES = ['ESPIROMETRIA', 'SPIROMETRY_COMBINED', 'SPIROMETRY_COMBINED_BRONCHODILATOR'];
const AUDIOMETRY_CODES = ['AUDIOMETRIA', 'AUDIOMETRIA_BILATERAL'];

const IAResultsWrapper = ({ getAsset, item, inputs, fullInputs, type, fileList, patient }) => {
  let examInfo;
  let document = { id: item.result_id, type: 'AdmissionProcedureResult' };
  const patientInfo = {
    branch_office_id: patient?.branch_office_id,
    admission_patient_id: inputs?.admission_patient_id,
  };
  if (type?.startsWith('spiro')) {
    examInfo = {
      patient: patientInfo,
      exams: [{
        inputs: {
          procedures: inputs?.procedures,
        }
      }]
    };
  } else if (type === 'audio') {
    examInfo = {
      patient: patientInfo,
      exams: [{
        inputs: {
          procedures: (fullInputs?.procedures || []).filter((procedure) =>
            ['thresholds', 'masking'].includes(procedure.group)
          ),
        }
      }]
    };
  }

  return (
    <IAResults
      getAsset={getAsset}
      document={document}
      examInfo={examInfo}
      type={type}
      file={fileList[item.id]?.[0]}
    />
  );
};

const NormalForm = ({
  inputs,
  fullInputs,
  setFileList,
  fileList,
  loadingFiles,
  toDoctor,
  disableForm,
  readOnly = false,
  roles,
  status,
  form,
  patient,
  removeDocument,
  convertFile,
  convertError,
  isDICOM,
  handleConvertFile,
  imageUrl,
  setImageUrl,
  countrySettings,
  isGroupedExams,
  enableEcg,
  admissionPatientExamId,
  setRefresh,
  fromExternal,
  allExams,
  vigentExamResultIds,
  onVigentExamFileChange,
}) => {
  const countryCode = getCookie('country_code');
  const user = getCookie('user');
  const [calculatedFields, setCalculatedFields] = useState(new Set());
  const [loadingCalculation, setLoadingCalculation] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    if (!enableEcg || !admissionPatientExamId) return;
    const socket = io(config.socketServer);
    socketRef.current = socket;
    const socketType = `ecg-${admissionPatientExamId}`;
    socket.on(socketType, (data) => {
      if (data) {
        setRefresh?.(Date.now());
        window.location.reload();
      }
    });
    return () => socket.disconnect();
  }, [enableEcg, admissionPatientExamId, setRefresh]);

  const handleInputChange = (event, item) => {
    if (form && inputs.measurements && inputs.procedures && inputs.code === 'ANTROPOMETRIA') {
      imcCalculator(form, inputs, countrySettings.details.imcDecimal[countryCode]);
      pamCalculator(form, inputs);
      arterialPresionCalculator(form, inputs);
      arterialWaistHipCalculators(form, inputs);

      const iccResult = iccCalculator(form, inputs);
      if (iccResult) {
        setCalculatedFields(prev => {
          const next = new Set(prev);
          if (iccResult.shouldRemove) {
            next.delete(iccResult.calculatedFieldId);
          } else {
            next.add(iccResult.calculatedFieldId);
          }
          return next;
        });
      }
    }

    if (form && (inputs.code === 'HISTORY')) {
      const occupationalHistoryResults = occupationalHistoryTimeCalculator(form, inputs);

      if (occupationalHistoryResults && Array.isArray(occupationalHistoryResults)) {
        setCalculatedFields(prev => {
          const next = new Set(prev);

          occupationalHistoryResults.forEach(result => {
            if (result.shouldRemove) {
              next.delete(result.calculatedFieldId);
            } else {
              next.add(result.calculatedFieldId);
            }
          });

          return next;
        });
      }
    }

    if (form && inputs?.procedures
      && countrySettings.details.examsCalculatorResults[countryCode]
      && inputs.code === 'ANTROPOMETRIA') {
      nutritionalDiagnosis(form, inputs);
      bloodPressureDiagnosis(form, inputs);
    }

    if (form && inputs?.questions && ['ENCUESTA_SALUD_2025', 'DECLARACION_SALUD_ISL'].includes(inputs.code)) {
      packsPerYearCalculator(form, inputs);
    }
  }

  const handleSelectInputChange = (event, item) => {
    if (form && inputs?.procedures
      && inputs.code === 'BUSS_PERRY_PSICO_PUNTAJE') {
      bussPerryInterpretation(form, inputs);
    }

    if (form && inputs?.questions && inputs.code === 'DECLARACION_SALUD_ISL') {
      heartDiseasesCalculator(form, inputs, item, event);
      lungDiseasesCalculator(form, inputs, item, event);
    }

    if (item.code === 'ENFERMEDADES_1_PATOLOGICOS') {
      pathologicalDescriptionsCalculator(form, inputs, item, event);
    }
  }

  const handleTextInputChange = (value, item) => {
    if (form && inputs?.questions && inputs.code === 'DECLARACION_SALUD_ISL') {
      lungDiseasesCalculator(form, inputs, item, value);
    }

    if (form && inputs?.questions && item.code && item.code.endsWith('_DESCRIPCION_1_PATOLOGICOS')) {
      pathologicalDescriptionsCalculator(form, inputs, item, value);
    }
  }

  const handleCalculationAndDisable = async (item, value) => {
    const calculationResults = await handleProcedureCalculation(
      item.code,
      value,
      inputs.admission_patient_id,
      form,
      inputs.procedures,
      user
    );
    if (!calculationResults) {
      return;
    }
    const newCalculatedIds = calculationResults
      .map(result => result?.calculatedFieldId)
      .filter(Boolean);


    if (newCalculatedIds.length > 0) {
      setCalculatedFields(prev => {
        return new Set([...prev, ...newCalculatedIds]);
      });
    }
  };

  const debouncedCalculation = debounce(async (item, value) => {
    setLoadingCalculation(true);
    try {
      await handleCalculationAndDisable(item, value);
    } catch (error) {
      console.error(error);
      message.error({
        content: `Error interno: ${error}`,
        style: {
          marginTop: "35vh",
          fontSize: 16,
        },
      });
    } finally {
      setLoadingCalculation(false);
    }
  }, 500);

  const handleDownload = async (file) => {
    const url = await getUrl(file);
    const link = document.createElement("a");
    link.setAttribute('download', file.name);
    link.href = url;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  }

  const getUrl = async (file) => {
    let url;
    try {
      if (file.uid === '-1') {
        const { document_url } = await getAsset({
          field: file.field,
          id: file.id,
          type: file.document_type,
        });
        url = document_url;
      } else {
        const blob = new Blob([file], { type: file.type });
        url = window.URL.createObjectURL(blob);
      }
    } catch (error) {
      console.error(error);
      message.error({
        content: `Error interno: ${error}`,
        style: {
          marginTop: "35vh",
          fontSize: 16,
        },
      });
    }
    return url;
  };

  const handleDicomPreview = async (files) => {
    if (files) {
      const [file] = files;
      const newWindow = window.open(`${config.dicomUrl}?url=${await getUrl(file)}`, '_blank', 'noopener,noreferrer')
      if (newWindow) {
        newWindow.opener = null;
      }
    }
  };

  const hasFileToConvert = (procedure) => {
    if (procedure?.id) {
      const file = fileList[procedure.id]?.[0];
      return file && (file.type === 'application/pdf' || isDICOM(file));
    }
  }

  const canShowConvertButton = (procedure) => {
    const toConvertProcedure = inputs?.procedures?.find(procedure => procedure?.code?.includes('IMAGE_'));

    return (
      hasFileToConvert(procedure) && convertError
    ) ||
      (
        hasFileToConvert(procedure) &&
        !hasFileToConvert(toConvertProcedure)
      )
  }

  const getFileUrl = useCallback(async (toConvertProcedure) => {
    const file = fileList[toConvertProcedure.id]?.[0];
    if (file) {
      const url = await getUrl(file);
      setImageUrl(prevState => ({
        ...prevState,
        [toConvertProcedure.id]: url,
      }));
    }
  }, [fileList]);

  const canConvertFile = (item) => {
    return item.code?.includes('IMAGE_') && !fileList[item.id]?.[0]
  }

  useEffect(() => {
    if (inputs?.procedures?.length) {
      const toConvertProcedures = inputs.procedures?.filter(procedure => procedure.code?.includes('IMAGE_'));
      if (toConvertProcedures.length) {
        for (let toConvertProcedure of toConvertProcedures) {
          getFileUrl(toConvertProcedure);
        }
      }
    }
  }, [getFileUrl, inputs.procedures, setFileList]);

  const getOrderedInputTypes = () => {
    const defaultOrder = ['measurements', 'procedures', 'surveys'];
    const order = inputs?.extra_data_exam?.input_types_order || defaultOrder;

    let orderedTypes = [];

    order.forEach(typeKey => {
      if (typeKey === 'measurements') {
        orderedTypes.push({ input_name: 'measurements' });
      } else if (typeKey === 'procedures') {
        orderedTypes.push({ input_name: 'procedures' });
      } else if (typeKey === 'surveys') {
        if (inputs.surveys) {
          orderedTypes.push(...inputs.surveys.map(survey => ({ ...survey, input_name: 'questions' })));
        }
      }
    });

    return orderedTypes;
  };

  const statusText = (item) => {
    let text = '';
    if (item?.result_type?.includes('altered') && item.criterion) {
      text = ` - Alterado (${item.criterion})`
    }
    if (item?.result_type?.includes('reproves')) {
      text = `${text} | Reprobatorio empresa`
    }
    return (<div className="custom_altered">{text}</div>)
  }

  return (
    <div className={`normal-form-container ${isGroupedExams ? 'is-grouped' : ''}`}>
      {
        getOrderedInputTypes().map((type, typeIndex) => {
          const rawInputs = type.input_name === 'questions'
            ? inputs.questions.filter(question => question.survey_id === type.id)
            : inputs[type.input_name];

          if (!rawInputs || rawInputs.length === 0) return null;

          const itemsWithLayout = calculateFormLayoutSpans(rawInputs, inputs?.form_layout, fromExternal, allExams?.map(exam => exam?.inputs?.form_layout));
          const checkVisible = (item) => {
            const isDoctor = roles.includes('doctor') || roles.includes('direccion_medica');
            const isPsychologist = roles.includes('equipo_psicologia');
            const isVocalHandicapDoctor = fromExternal && isDoctor && inputs.code === 'TEST_INCAPACIDAD_VOCAL';
            return (!item.blocked && !item.medical_visibility) || (!item.blocked && isPsychologist) || (item.medical_visibility && isDoctor) || (!item.blocked && isVocalHandicapDoctor);
          };
          if (!itemsWithLayout.some(checkVisible)) return null;
          const dependentLookup = buildDependentLookup(inputs?.form_layout);

          const columnCount = getFormColumnCount(inputs?.form_layout, allExams?.map(exam => exam?.inputs?.form_layout));
          const useGrouping = columnCount > 1;
          let itemsToRender = itemsWithLayout;
          let childrenMap = {};
          if (useGrouping) {
            const { topLevelItems, childrenMap: groupedChildren } = groupDependentItems(rawInputs, inputs?.form_layout);
            itemsToRender = calculateFormLayoutSpans(topLevelItems, inputs?.form_layout, fromExternal, allExams?.map(exam => exam?.inputs?.form_layout));
            childrenMap = groupedChildren;
          }

          return (
            <Fragment key={typeIndex}>
              {
                type.input_name === 'questions' && (
                  <Col lg={24} xs={24} className={`${isGroupedExams ? 'mb-8' : 'mb-24'} ${typeIndex > 0 ? (isGroupedExams ? 'mt-32' : 'mt-60') : ''}`}>
                    {isGroupedExams ? <p className="text-exam-title">{type.name}</p> : <h2><strong>{type.name}</strong></h2>}
                  </Col>
                )
              }
              <Card
                className="form-section-card mb-32"
                bordered={true}
              >
                {
                  type.detail_description && <div className="mb-20">
                    <Alert
                      message={<div dangerouslySetInnerHTML={{ __html: type.detail_description }} />}
                      type="info"
                    />
                  </div>
                }
                <Row gutter={[16, 24]} className="aligned-form">
                  {
                    itemsToRender.sort((first, second) => first.order - second.order).map(item => {
                      const isDoctor = roles.includes('doctor') || roles.includes('direccion_medica');
                      const isPsychologist = roles.includes('equipo_psicologia');
                      const isVocalHandicapDoctor = fromExternal && isDoctor && inputs.code === 'TEST_INCAPACIDAD_VOCAL';
                      const visible = (!item.blocked && !item.medical_visibility) || (!item.blocked && isPsychologist) || (item.medical_visibility && isDoctor) || (!item.blocked && isVocalHandicapDoctor);

                      const renderInputItem = (fieldItem, isDependentFieldFlag) => {
                        const fieldCalculated = calculatedFields.has(fieldItem.id);
                        const fieldDisableCondition = readOnly || !((isDoctor && toDoctor) || (!disableForm && !isDoctor) || (isDoctor && fieldItem.medical_visibility) || (!disableForm && isPsychologist) || isVocalHandicapDoctor);

                        return (
                          <Form.Item
                            key={`${type.input_name}-${fieldItem.id}`}
                            onChange={handleInputChange}
                            label={
                              <span>
                                <span dangerouslySetInnerHTML={{ __html: `${fieldItem.name}${fieldItem.unit && fieldItem.unit !== '' ? ` [${fieldItem.unit}]` : ''} ` }} />
                                {vigentExamResultIds?.has(fieldItem.id) && (
                                  <Tag className="vigent-exam ml-10">Vigente</Tag>
                                )}
                                {
                                  countrySettings.StatusDetail[countryCode] &&
                                  statusText(fieldItem)
                                }
                              </span>
                            }
                            className={`${countryCode} ${status(fieldItem, type)}${isDependentFieldFlag ? ' dependent-field' : ''}`}
                            style={isDependentFieldFlag ? { marginTop: 8 } : undefined}
                          >
                            {!['file'].includes(fieldItem.input_type) && (
                              <InputRenderers
                                item={fieldItem}
                                type={type}
                                disableCondition={fieldDisableCondition}
                                isCalculatedField={fieldCalculated}
                                loadingCalculation={loadingCalculation}
                                handleInputChange={handleInputChange}
                                handleSelectInputChange={handleSelectInputChange}
                                handleTextInputChange={handleTextInputChange}
                                debouncedCalculation={debouncedCalculation}
                                countryCode={countryCode}
                                countrySettings={countrySettings}
                              />
                            )}
                            {fieldItem.input_type === 'file' && loadingFiles && (
                              <Spin className="cmt-spin" spinning={loadingFiles?.[fieldItem.id] || false}>
                                <Form.Item>
                                  <Upload.Dragger
                                    name="file"
                                    className="mt-10"
                                    disabled={fieldDisableCondition}
                                    onRemove={async () => {
                                      setFileList(await removeDocument(fieldItem, fileList));
                                      onVigentExamFileChange?.(fieldItem.id);
                                    }}
                                    beforeUpload={(file) => {
                                      onVigentExamFileChange?.(fieldItem.id);
                                      setFileList((list) => ({ ...list, [fieldItem.id]: [file] }));
                                      handleConvertFile(file, fieldItem);
                                      return false;
                                    }}
                                    fileList={fileList && fileList[fieldItem.id]}
                                    onPreview={handleDownload}
                                  >
                                    <p className="ant-upload-text">Presione o arrastre su documento a esta área para subirlo</p>
                                  </Upload.Dragger>
                                </Form.Item>
                                <Row gutter={[10, 0]}>
                                  {
                                    <Col>
                                      {fileList && fileList[fieldItem.id] && fileList[fieldItem.id][0]?.uid === '-1' && isDICOM(fileList[fieldItem.id][0]) && (
                                        <Button onClick={() => handleDicomPreview(fileList[fieldItem.id])}>Previsualizar Dicom</Button>
                                      )}
                                    </Col>
                                  }
                                  {fileList[fieldItem.id]?.[0] &&
                                    fieldItem.code?.includes('DOCUMENT_') &&
                                    canShowConvertButton(fieldItem) &&
                                    hasFileToConvert(fieldItem) &&
                                    canConvertFile(fieldItem) &&
                                    countryCode === 'PE' && (
                                      <Col>
                                        <Button
                                          className="cmt-button"
                                          type="primary"
                                          onClick={async () => convertFile(fieldItem, fileList[fieldItem.id]?.[0], await getUrl(fileList[fieldItem.id]?.[0]))}
                                        >
                                          Convertir archivo a imágen
                                        </Button>
                                      </Col>
                                    )}
                                  {/* {fieldItem.code === 'DOCUMENT_EKG' &&
                                    (roles.includes('doctor') || roles.includes('direccion_medica')) && (
                                      <ECGInterpreter
                                        source={fileList[fieldItem.id]?.[0]}
                                        getUrl={getUrl}
                                        query="What are the main features in this ECG image?"
                                        onAnalyzed={() => { }}
                                      />
                                    )} */}
                                  {inputs && countrySettings?.spirometry?.aiButton?.[countryCode] && fileList[fieldItem.id]?.[0]
                                    && fieldItem.code?.includes('DOCUMENT_') && SPIROMETRY_CODES.includes(inputs.code) && (
                                      <Col>
                                        <IAResultsWrapper
                                          getAsset={getAsset}
                                          item={fieldItem}
                                          fileList={fileList}
                                          inputs={inputs}
                                          patient={patient}
                                          type={inputs.code === 'ESPIROMETRIA' ? 'spiro' : 'spiro_with_predicted'}
                                        />
                                      </Col>
                                    )}
                                    {inputs && countrySettings?.audiometry?.aiButton?.[countryCode] && fileList[fieldItem.id]?.[0]
                                      && fieldItem.code?.includes('DOCUMENT_') && AUDIOMETRY_CODES.includes(inputs.code)
                                      && !fromExternal && (
                                        <Col>
                                          <IAResultsWrapper
                                            getAsset={getAsset}
                                            item={fieldItem}
                                            fileList={fileList}
                                            inputs={inputs}
                                            fullInputs={fullInputs}
                                            patient={patient}
                                            type={'audio'}
                                          />
                                        </Col>
                                      )}
                                </Row>
                                {fieldItem.code?.includes('IMAGE_') && fileList[fieldItem.id]?.[0] && (
                                  <Image width={200} src={imageUrl[fieldItem.id]} loading={!imageUrl[fieldItem.id]} />
                                )}
                                {
                                  (fromExternal && countrySettings.medicalConsultation.previewFiles[countryCode]) &&
                                  <FilePreviewer
                                    file={fileList[fieldItem.id]?.[0]}
                                    getUrl={getUrl}
                                  />
                                }
                              </Spin >
                            )}
                          </Form.Item>
                        );
                      };

                      return (
                        <Fragment key={`${type.input_name}-${item.id}`}>
                          <Form.Item shouldUpdate noStyle>
                            {() => {
                              const layoutVisible = evaluateVisibility(item, type.input_name, form?.getFieldValue, inputs?.form_layout, patient, rawInputs);
                              if (!visible || !layoutVisible) return null;
                              const isDependent = isDependentField(item, dependentLookup);
                              return (
                                <Col {...item.spans}>
                                  {renderInputItem(item, isDependent)}
                                  {useGrouping && (childrenMap[item.code] || childrenMap[item.id] || []).map((child, index) => {
                                    const childVisible = (!child.blocked && !child.medical_visibility) || (!child.blocked && isPsychologist) || (child.medical_visibility && isDoctor) || (!child.blocked && isVocalHandicapDoctor);
                                    const childLayoutVisible = evaluateVisibility(child, type.input_name, form?.getFieldValue, inputs?.form_layout, patient, rawInputs);
                                    if (!childVisible || !childLayoutVisible) return null;

                                    const childIsDependent = isDependentField(child, dependentLookup);
                                    return (
                                      <Fragment key={`child-${child.id}-${index}`}>
                                        {renderInputItem(child, childIsDependent)}
                                      </Fragment>
                                    );
                                  })}
                                </Col >
                              );
                            }}
                          </Form.Item >
                        </Fragment >
                      );
                    })
                  }
                </Row >
              </Card >
            </Fragment >
          );
        })
      }
    </div >
  );
};

export default NormalForm;
